import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { publishReplay, refCount, map, mapTo } from 'rxjs';
import { HttpParams, HttpClient } from '@angular/common/http';
import { UrlBuilderService } from '@common/services/url-builder.service';
import {
  SavePmaDocumentCentreDocumentPropertiesRequest,
  PmaDocumentCentreDocumentProperties,
  SavePmaDocumentCentreWidgetConfigurationRequest,
  PmaDocumentCentreConfiguration
} from '@feature/pma-document-centre/pma-document-centre-types';
import { ITable } from '@common/models/table';
import { UploadRequest, EntityKey } from '@feature/pma-document-centre/pma-document-centre-types';
import { SelectItem } from 'primeng/api';
import { EntityLookupItemDto } from '@feature/pma-entity/pma-entity-types';
import { audit, auditItem } from '@common/models/audit-item';
import {NameValueDto} from "@common/models/name-value";

@Injectable({
  providedIn: 'root'
})
export class PmaDocumentCentreWidgetHttpService {

  private _linkableApps: Observable<any>;

  constructor(
    private readonly urlProvider: UrlBuilderService,
    private readonly http: HttpClient
  ) {
  }

  getWidgetConfiguration(widgetConfigurationId: number) {
    const url = this.urlProvider.getUrl(`document-centre/config/${widgetConfigurationId}`);
    return this.http.get<PmaDocumentCentreConfiguration>(url);
  }

  copyWidgetConfiguration(request: SavePmaDocumentCentreWidgetConfigurationRequest) {
    const url = this.urlProvider.getUrl('document-centre/config/copy');
    return this.http.post(url, request).pipe(mapTo(true));
  }

  saveWidgetConfiguration(request: SavePmaDocumentCentreWidgetConfigurationRequest) {
    const url = this.urlProvider.getUrl('document-centre/config');
    return this.http.post(url, request).pipe(mapTo(true));
  }

  getWidgetTableData(request: any): Observable<ITable> {
    const url = this.urlProvider.getUrl('document-centre-widget/table');
    return this.http.post<ITable>(url, request);
  }

  deleteDocument(key: EntityKey) {
    const url = this.urlProvider.getUrl('document-centre/delete');
    return this.http.request('delete', url, { body: key });
  }

  downloadDocument(key: EntityKey) {
    const url = this.urlProvider.getUrl('document-centre/download');
    return this.http.post(url, key, { responseType: 'blob' });
  }

  linkableTypes() {
      const url = this.urlProvider.getUrl('document-centre/linkable-items');
      return this.http.get<NameValueDto[]>(url).pipe(
        map(
          dtos => dtos.map((dto) => ({ label: dto.Name, value: dto.Value }))
        )
      );
  }

  getLinkableApplications() {

    if (!this._linkableApps) {
      const url = this.urlProvider.getUrl('document-centre/linkable-applications');
      this._linkableApps = this.http.get<any>(url).pipe(
        map(r => r.Applications),
        publishReplay(1),
        refCount());
    }

    return this._linkableApps;

  }

  getUploadableCategories(widgetConfigurationId: number): Observable<SelectItem[]> {
    const url = this.urlProvider.getUrl(`document-centre/uploadcategories`);
    const params = new HttpParams({
      fromObject: {
        widgetConfigurationId: widgetConfigurationId.toString()
      }
    });

    return this.http.get<EntityLookupItemDto[]>(url, { params: params }).pipe(map(
      dtos => dtos.map((dto) => ({ label: dto.Name, value: dto.Key }))
    ));
  }

  getAudit(entityKey: any): Observable<auditItem[]> {
    const url = this.urlProvider.getUrl(`document-centre/audit`);
    return this.http.post<audit>(url, entityKey).pipe(map( (audit) => { return audit.Logs; }
    ));
  }

  uploadFile(request: UploadRequest) {
    const url = this.urlProvider.getUrl(`document-centre/upload`);
    const formData = new FormData();
    formData.append('file', request.file);
    formData.append('model', JSON.stringify(request.model));
    return this.http.post(url, formData);
  }

  getDocumentProperties(key: EntityKey): Observable<PmaDocumentCentreDocumentProperties> {
    const url = this.urlProvider.getUrl('document-centre/document');
    return this.http.post<any>(url, key).pipe(map(r => r.DocumentCentreDocumentProperties));
  }

  saveDocumentProperties(saveRequest: SavePmaDocumentCentreDocumentPropertiesRequest) {
    const url = this.urlProvider.getUrl('document-centre/document/update');
    return this.http.post<any>(url, saveRequest);
  }


}
